// PersonalInfo.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function RenderLabel({ htmlFor, children }) {
  return (
    <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground mb-2">
      <span>{children}</span>
      <span className="text-destructive ml-1">*</span>
    </Label>
  );
}

const shallowEqualObj = (a = {}, b = {}) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (let k of aKeys) if ((a[k] ?? '') !== (b[k] ?? '')) return false;
  return true;
};

export default function PersonalInfo({ onUpdate, data = {} }) {
  const initial = {
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    phoneNumber: data?.phoneNumber ?? '',
    emailId: data?.emailId ?? '',
    linkedInLink: data?.linkedInLink ?? '',
    gitHub: data?.gitHub ?? '',
    portfolio: data?.portfolio ?? '',
    currentCtc: data?.currentCtc ?? '',
    expectedCtc: data?.expectedCtc ?? '',
    noticePeriod: data?.noticePeriod ?? '',
    currentLocation: data?.currentLocation ?? '',
    currentOrganization: data?.currentOrganization ?? ''
  };

  const [personalInfo, setPersonalInfo] = useState(() => ({ ...initial }));
  const [touched, setTouched] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const mountedRef = useRef(false);

  // Sync when parent data changes, but ONLY when user isn't editing.
  useEffect(() => {
    if (!mountedRef.current) {
      // first mount - initialize once
      setPersonalInfo({ ...initial });
      mountedRef.current = true;
      return;
    }

    if (!isEditing) {
      // avoid unnecessary re-renders: only update if data changed
      const incoming = {
        firstName: data?.firstName ?? '',
        lastName: data?.lastName ?? '',
        phoneNumber: data?.phoneNumber ?? '',
        emailId: data?.emailId ?? '',
        linkedInLink: data?.linkedInLink ?? '',
        gitHub: data?.gitHub ?? '',
        portfolio: data?.portfolio ?? '',
        currentCtc: data?.currentCtc ?? '',
        expectedCtc: data?.expectedCtc ?? '',
        noticePeriod: data?.noticePeriod ?? '',
        currentLocation: data?.currentLocation ?? '',
        currentOrganization: data?.currentOrganization ?? ''
      };
      if (!shallowEqualObj(incoming, personalInfo)) {
        setPersonalInfo(incoming);
        setTouched({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditing]);

  // propagate upward when local state changes (debounce not required here)
  useEffect(() => {
    onUpdate && onUpdate(personalInfo);
  }, [personalInfo]); // Removed onUpdate from dependencies to prevent re-render loop

  const handleChange = (e) => {
    const { id, value } = e.target;
    // keep values as strings — parent will save/convert as needed
    setPersonalInfo((prev) => ({ ...prev, [id]: value }));
  };

  const handleBlur = (e) => {
    const { id } = e.target;
    setTouched((prev) => ({ ...prev, [id]: true }));
    // stop editing when any field blurs
    setIsEditing(false);
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const renderInput = (id, placeholder, type = 'text', step, min, max) => {
    const val = personalInfo[id] ?? '';
    const hasError = touched[id] && !val?.toString().trim();
    return (
      <>
        <Input
          type={type}
          id={id}
          className={cn(
            hasError && "border-destructive focus-visible:ring-destructive"
          )}
          placeholder={placeholder}
          value={val}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          required
          aria-required="true"
          step={step}
          min={min}
          max={max}
        />
        {hasError && (
          <Text size="sm" variant="destructive" className="mt-1.5">
            This field is required
          </Text>
        )}
      </>
    );
  };

  return (
    <Card className="mb-6 bg-white">
      <CardContent className="p-4">
        <Heading level="h2" className="text-base font-semibold text-foreground mb-3">
          Personal Info.
        </Heading>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex flex-col">
            <RenderLabel htmlFor="firstName">First Name</RenderLabel>
            {renderInput('firstName', 'Ex. Lily')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="lastName">Last Name</RenderLabel>
            {renderInput('lastName', 'Ex. Relish')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="phoneNumber">Phone Number</RenderLabel>
            {renderInput('phoneNumber', '0000000000', 'tel')}
          </div>
        </div>

        <Heading level="h3" className="text-sm font-semibold text-foreground mb-3">
          Professional Details
        </Heading>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex flex-col">
            <RenderLabel htmlFor="currentCtc">Current CTC (LPA)</RenderLabel>
            {renderInput('currentCtc', 'Ex. 12.5', 'number', '0.1', '0')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="expectedCtc">Expected CTC (LPA)</RenderLabel>
            {renderInput('expectedCtc', 'Ex. 18.0', 'number', '0.1', '0')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="noticePeriod">Notice Period (Days)</RenderLabel>
            {renderInput('noticePeriod', 'Ex. 30', 'number', undefined, '0', '365')}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex flex-col">
            <RenderLabel htmlFor="currentLocation">Current Location</RenderLabel>
            {renderInput('currentLocation', 'Ex. Bangalore, Karnataka')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="currentOrganization">Current Organization</RenderLabel>
            {renderInput('currentOrganization', 'Ex. Google India')}
          </div>
        </div>

        <Heading level="h3" className="text-lg font-semibold text-foreground mb-4">
          Online Presence
        </Heading>
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col">
            <RenderLabel htmlFor="linkedInLink">LinkedIn Link</RenderLabel>
            {renderInput('linkedInLink', 'www.linkedin.com/in/example000000', 'url')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="gitHub">GitHub</RenderLabel>
            {renderInput('gitHub', 'https://github.com/example000', 'url')}
          </div>

          <div className="flex flex-col">
            <RenderLabel htmlFor="portfolio">Portfolio</RenderLabel>
            {renderInput('portfolio', 'www.example0000.com', 'url')}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}